import { Link } from 'react-router-dom';
import { FaTools } from 'react-icons/fa';
import CTASection from '../../components/CTASection/CTASection';
import PageHero from '../../components/PageHero/PageHero';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import { PRACTICES, practicePath } from '../../content/services';
import { PAGE_SEO, PRIMARY_NAV } from '../../content/site';
// Already in the bundle for /services, so the 404 adds no image weight.
import heroImage from '../../assets/servicesb-hero.jpg';
import { SERVICE_ICONS } from './ServiceDetail';
import styles from './NotFound.module.scss';

/**
 * The catch-all route. Before this, any unknown URL rendered the header, an
 * empty <main>, and the footer -- a typo, a stale link, or an old campaign URL
 * all produced a blank white page with no way back (audit P0-5).
 *
 * Built from the same pieces as the rest of the site: PageHero, the shared
 * container, and CTASection. The destinations come from PRIMARY_NAV and
 * PRACTICES rather than a hand-written list, so this page cannot start
 * offering routes that no longer exist -- which is the specific way a 404 page
 * rots.
 *
 * NOTE ON THE HTTP STATUS. This renders inside a client-side SPA, so the
 * server still answers 200 for the missing URL and this is a soft 404. The
 * <Seo> entry is `noIndex`, which is what stops it being indexed. A real 404
 * status needs a rewrite rule, and those live only in the unversioned Amplify
 * console (audit P1-10).
 */
const NotFoundPage = () => (
  <div className={styles.notFoundPage}>
    <Seo {...seoPropsFor(PAGE_SEO.notFound)} />

    <div className="main-content">
      <PageHero
        title="Page Not Found"
        subtitle="The page you were looking for has moved, or the link that brought you here is out of date."
        image={heroImage}
        tone="notFound"
      />

      <section className={styles.wayfinding}>
        <div className={styles.container}>
          <h2>Where to go from here</h2>

          <ul className={styles.linkGrid}>
            {PRIMARY_NAV.map((item) => (
              <li key={item.path}>
                <Link to={item.path} className={styles.linkCard}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <h3 className={styles.practiceHeading}>Or go straight to a practice</h3>

          <ul className={styles.practiceGrid}>
            {PRACTICES.map((practice) => {
              // The practice's first service supplies the icon, so the cards
              // carry the same visual language as the hub they link to.
              const [lead] = practice.services;
              const Icon = SERVICE_ICONS[lead.iconKey] || FaTools;
              return (
                <li key={practice.slug}>
                  <Link to={practicePath(practice)} className={styles.practiceCard}>
                    <Icon className={styles.practiceIcon} aria-hidden="true" />
                    <span className={styles.practiceName}>{practice.name}</span>
                    <span className={styles.practiceDescription}>
                      {practice.navDescription}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </div>

    <CTASection
      heading="Still not finding it?"
      body="Tell us what you were looking for and we will point you to the right place."
      ctaLabel="Contact Us"
      ctaHref="/contact"
    />
  </div>
);

export default NotFoundPage;
